import React from 'react'
import styled from 'styled-components'
import TimeSeriesComparison from './timeSeriesComparison'
import { Wrapper } from '../styles'
import {
  TimeSeriesComparisonBarsProps,
  TimeSeriesComparisonBarsPayload,
  SeriesInput,
  SeriesNormalized,
} from '../visualization.types'
import { stripParens } from '../utils/format'
import { buildComparisonBarsModel } from '../utils/comparisonBars'

const BarsPreview = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 10px;
  width: 100%;
  padding: 10px 14px 10px var(--bar-preview-padding-left, 16px);
  margin-left: var(--bar-preview-offset, -12px);
`

const BarRow = styled.div`
  display: grid;
  grid-template-columns: var(--bar-label-width, 44px) minmax(48px, 1fr) var(
      --bar-value-width,
      8.5ch
    );
  align-items: center;
  width: 100%;
  column-gap: 8px;
`

const BarLabel = styled.span`
  width: var(--bar-label-width, 44px);
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  text-align: right;
  white-space: nowrap;
  box-sizing: border-box;
  padding-right: 0px;
`

const BarTrack = styled.div`
  position: relative;
  height: 12px;
  border-radius: 999px;
  background: #e5e7eb;
  overflow: hidden;
`

const BarFill = styled.div<{
  $width: number
  $color: string
  $animated?: boolean
  $animationDelay?: number
}>`
  --bar-fill-width: ${({ $width }) => `${$width}%`};

  width: ${({ $animated, $width }) => ($animated ? '0%' : `${$width}%`)};
  height: 100%;
  background: ${({ $color }) => $color};
  border-radius: inherit;

  @keyframes fillAnimation {
    from {
      width: 0%;
    }
    to {
      width: var(--bar-fill-width);
    }
  }

  ${({ $animated, $animationDelay }) =>
    $animated
      ? `
    animation: fillAnimation 0.5s ease-out ${$animationDelay ?? 0}ms forwards;
  `
      : ''}
`

const BarValue = styled.span<{ $fontSize: string }>`
  font-size: ${({ $fontSize }) => $fontSize};
  font-weight: 600;
  color: #374151;
  text-align: left;
  white-space: nowrap;
  width: var(--bar-value-width, 8.5ch);
`

export default function TimeSeriesComparisonBars(props: TimeSeriesComparisonBarsProps) {
  const {
    visualization,
    isExpanded,
    isMaximized,
    activeButton,
    activeLabel,
    insightTitle,
    animated,
    animationDelay,
  } = props
  const payload = visualization?.payload as TimeSeriesComparisonBarsPayload

  if (isExpanded) {
    return (
      <TimeSeriesComparison
        visualization={visualization}
        isExpanded={isExpanded}
        isMaximized={isMaximized}
        activeButton={activeButton}
        activeLabel={activeLabel}
        insightTitle={insightTitle}
      />
    )
  }

  const rawSeries: SeriesInput[] = Array.isArray(payload.series) ? payload.series : []
  const series: SeriesNormalized[] = rawSeries.map((serie, index) => ({
    name: stripParens(serie.name ?? serie.label) ?? `Série ${index + 1}`,
    color: serie.color ?? '#2563eb',
    fillColor: serie.fillColor,
    values: (serie.y ?? []) as number[],
    year: serie.year as number | undefined,
    labelRaw: (serie.label ?? serie.name ?? '') as string,
  }))

  const model = buildComparisonBarsModel(payload, series, insightTitle)

  if (!model) {
    return (
      <TimeSeriesComparison
        visualization={visualization}
        isExpanded={isExpanded}
        isMaximized={isMaximized}
        activeButton={activeButton}
        activeLabel={activeLabel}
        insightTitle={insightTitle}
      />
    )
  }

  const chartHeight = 60
  const toPercent = (value: number) =>
    model.maxAbsValue > 0
      ? Math.max(0, Math.min(100, (Math.abs(value) / model.maxAbsValue) * 100))
      : 0
  const maxValueChars = Math.max(...model.rows.map(row => row.valueText.length), 1)
  const valueWidthSteps = [
    { maxChars: 5, width: '3ch' },
    { maxChars: 10, width: '5.5ch' },
    { maxChars: 12, width: '9.5ch' },
    { maxChars: 14, width: '12ch' },
  ]
  const valueWidth = valueWidthSteps.find(step => maxValueChars <= step.maxChars)?.width ?? '12ch'
  const valueFontSize = maxValueChars > 18 ? '12px' : '13px'
  const labelWidth = model.shouldUseMonthYearLabelOnLeft ? '64px' : '44px'

  return (
    <Wrapper $height={chartHeight}>
      <BarsPreview
        style={{
          ['--bar-value-width' as string]: valueWidth,
          ['--bar-label-width' as string]: labelWidth,
          ['--bar-preview-padding-left' as string]: model.shouldUseMonthYearLabelOnLeft
            ? '19px'
            : '16px',
          ['--bar-preview-offset' as string]: model.shouldUseMonthYearLabelOnLeft
            ? '-10px'
            : '-12px',
        }}
      >
        {model.rows.map((row, rowIndex) => (
          <BarRow key={row.key}>
            <BarLabel>{row.leftLabel}</BarLabel>
            <BarTrack title={row.title}>
              <BarFill
                $width={toPercent(row.value)}
                $color={row.color}
                $animated={animated}
                $animationDelay={animationDelay ? (animationDelay + 200) * rowIndex + 250 : 0}
              />
            </BarTrack>
            <BarValue $fontSize={valueFontSize}>{row.valueText}</BarValue>
          </BarRow>
        ))}
      </BarsPreview>
    </Wrapper>
  )
}
